import React, { useRef, useState } from "react";
import { Paperclip, Send, X, FileText, Loader2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SENDER_STYLE = {
  reporter: "items-start",
  agent: "items-end",
  system: "items-center",
};

export function DisputeThreadView({ messages, onSend, sending, side = "reporter", labels, locked = false, lockedText, composerTop, canSend = true, onRetract }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((a) => [...a, r.data]);
      } catch {}
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!text.trim() && !files.length) return;
    await onSend(text.trim(), files);
    setText("");
    setFiles([]);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4" data-testid="dispute-messages">
        {messages.map((m) => {
          const mine = m.sender === side;
          const isSystem = m.sender === "system";
          return (
            <div key={m.id} className={`flex flex-col ${SENDER_STYLE[m.sender] || "items-start"}`}>
              {isSystem ? (
                <div className="rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1 text-[11px] text-zinc-500">{m.text}</div>
              ) : (
                <div data-testid={`dispute-msg-${m.id}`} className={`group max-w-[80%] rounded-lg border px-3 py-2 ${m.retracted ? "border-dashed border-zinc-700 bg-zinc-900/40 opacity-70" : mine ? "border-blue-500/30 bg-blue-600/15" : "border-zinc-800 bg-zinc-900"}`}>
                  <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                    <span>{m.sender === "agent" ? (labels?.agent || "Agent") : (labels?.reporter || "Reporter")} · {m.author}</span>
                    {onRetract && m.sender === "agent" && !m.retracted && (
                      <button data-testid={`dispute-retract-${m.id}`} onClick={() => onRetract(m)} className="ml-auto inline-flex items-center gap-1 normal-case text-zinc-500 opacity-60 transition-opacity hover:text-red-300 group-hover:opacity-100">
                        <Undo2 size={11} /> Visszavonás
                      </button>
                    )}
                  </div>
                  {m.text && <div className={`whitespace-pre-wrap text-[13px] ${m.retracted ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{m.text}</div>}
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.attachments.map((a) => (
                        <a key={a.id} href={`${BACKEND}${a.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300 hover:bg-zinc-800">
                          <FileText size={11} /> {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleString()}</div>
                  {m.retracted && (
                    <div data-testid={`dispute-retracted-${m.id}`} className="mt-1 text-[10px] font-medium text-red-300/80">
                      Visszavonva · {m.retracted_by} · {new Date(m.retracted_at).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false })} · a bejelentő nem látja
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="shrink-0 border-t border-zinc-800/70 p-3">
        {locked ? (
          <div className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-3 text-center text-[12px] text-zinc-500" data-testid="dispute-locked">
            {lockedText || "This conversation is closed."}
          </div>
        ) : (
          <>
            {composerTop}
            {files.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {files.map((f) => (
                  <span key={f.id} className="inline-flex items-center gap-1 rounded border border-zinc-800 bg-zinc-950 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300">
                    {f.filename}
                    <button onClick={() => setFiles((a) => a.filter((x) => x.id !== f.id))} className="text-zinc-500 hover:text-red-400"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.gif,.webp,.pdf" onChange={addFiles} className="hidden" data-testid="dispute-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={!canSend} data-testid="dispute-attach" className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              </button>
              <Textarea
                data-testid="dispute-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={!canSend}
                placeholder={labels?.placeholder || "Write a message…"}
                className="min-h-[40px] max-h-32 flex-1 resize-none border-zinc-800 bg-zinc-950 text-[13px] disabled:opacity-50"
              />
              <Button onClick={send} data-testid="dispute-send" disabled={sending || uploading || !canSend} className="h-10 shrink-0 bg-blue-600 px-4 text-white hover:bg-blue-700">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
